import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

const CustomSelect = ({
    options = [],
    value,
    onChange, // expects (value, name) or just value depending on implementation. Let's stick to standard behavior: returns value.
    name,
    placeholder = 'Select an option',
    disabled = false,
    required = false,
    icon: Icon,
    className = '',
    searchable = true
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Find selected option object
    const selectedOption = options.find(opt =>
        (typeof opt === 'object' ? opt.value === value || opt._id === value : opt === value)
    );

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter options based on search
    const filteredOptions = options.filter(opt => {
        const label = typeof opt === 'object' ? (opt.label || opt.name) : opt;
        return String(label).toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleSelect = (opt) => {
        const val = typeof opt === 'object' ? (opt.value || opt._id) : opt;
        // Simulate standard event object if needed, or just pass value and name
        if (onChange) {
            // Create a synthetic event-like object or just pass the value directly if the parent expects that.
            // Looking at SubmitBusiness.js: handleChange(e) => setFormData({ ... [e.target.name]: e.target.value })
            // So we need to mimic an event object.
            onChange({
                target: {
                    name: name,
                    value: val
                }
            });
        }
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Hidden input for HTML5 validation */}
            <input
                type="text"
                name={name}
                value={value || ''}
                required={required}
                readOnly
                className="sr-only"
                tabIndex={-1}
                onChange={() => { }} // Suppress React warning
            />

            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`
          w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 dark:border-gray-700 
          bg-white dark:bg-gray-900 text-left cursor-pointer transition-all outline-none
          ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800' : 'hover:border-gray-300 dark:hover:border-gray-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'}
          ${isOpen ? 'ring-2 ring-blue-500/20 border-blue-500' : ''}
        `}
            >
                {Icon && (
                    <Icon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                )}

                <span className={`block truncate ${!selectedOption ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                    {selectedOption
                        ? (typeof selectedOption === 'object' ? (selectedOption.label || selectedOption.name) : selectedOption)
                        : placeholder}
                </span>

                <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
                </span>
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-900 shadow-xl max-h-60 rounded-xl py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm border border-gray-100 dark:border-gray-700">
                    {searchable && options.length > 5 && (
                        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}

                    {filteredOptions.length === 0 ? (
                        <div className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500 dark:text-gray-400 text-center py-4">
                            No options found
                        </div>
                    ) : (
                        filteredOptions.map((opt, index) => {
                            const optValue = typeof opt === 'object' ? (opt.value || opt._id) : opt;
                            const optLabel = typeof opt === 'object' ? (opt.label || opt.name) : opt;
                            const isSelected = value === optValue;

                            return (
                                <div
                                    key={optValue || index}
                                    className={`cursor-pointer select-none relative py-3 pl-10 pr-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors
                    ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}
                  `}
                                    onClick={() => handleSelect(opt)}
                                >
                                    <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                        {optLabel}
                                    </span>
                                    {isSelected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400">
                                            <Check className="w-4 h-4" />
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomSelect;
